import { DescriptionField } from "@dash/description-list";
import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

type Props = {
  userId: Uuid;
};

export const CustomerAccountDetails: React.FC<Props> = ({ userId }) => {
  const { isMobile } = useDeviceType();

  const navigate = useNavigate();

  const accountsAccountQuery = useClientManager("auth")
    .account()
    .useQuery({ uuid: userId }, { enabled: userId !== undefined });

  const pointsAccountQuery = useClientManager("loyaltyPoints")
    .account()
    .useQuery({ uuid: userId }, { enabled: userId !== undefined });

  const customerOrdersUrl = createUrl("/specjalista/orders", {
    userUuid: userId,
    page: 0,
    rewardId: [],
    isFulfilled: undefined,
  });

  const accountsUnauthorized = accountsAccountQuery.error === "Unauthorized";
  const pointsUnauthorized = pointsAccountQuery.error === "Unauthorized";

  if (accountsUnauthorized && pointsUnauthorized) {
    return null;
  }

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <SectionHeader
        isHr
        actions={
          <Button variant="outline" size="small" onClick={() => navigate(customerOrdersUrl)}>
            Realizacja nagród
          </Button>
        }
      >
        Dane konta
      </SectionHeader>
      {!accountsUnauthorized && (
        <QuerySkeleton query={accountsAccountQuery} height="90px">
          {(account) => (
            <div css={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.625rem" }}>
              <DescriptionField label="Data dołączenia">{account.joinedAt?.toLocaleString()}</DescriptionField>
              <DescriptionField label="Data rejestracji">{account.registeredAt?.toLocaleString()}</DescriptionField>
              {account.email && <DescriptionField label="Zatwierdzony email">{account.email}</DescriptionField>}
              {account.unverifiedEmail && (
                <DescriptionField label="Email do zatwierdzenia">{account.unverifiedEmail}</DescriptionField>
              )}
            </div>
          )}
        </QuerySkeleton>
      )}
      {!pointsUnauthorized && (
        <QuerySkeleton query={pointsAccountQuery} height="90px">
          {(account) => (
            <div css={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.625rem" }}>
              <DescriptionField label="Stan konta">{account.balance.toFixed(1)} pkt</DescriptionField>
              <DescriptionField label="Status">
                <StatusField
                  customerId={userId}
                  customerPhoneNumber={accountsAccountQuery.data?.phoneNumber}
                  isAffiliate={account.isAffiliate}
                  isPro={account.isPro}
                  onChange={() => pointsAccountQuery.refetch()}
                />
              </DescriptionField>
              <DescriptionField label="Nr konta PRO">{account.proNumber}</DescriptionField>
              <DescriptionField label="Liczba skanów">{account.lifetimeCodeCount}</DescriptionField>
            </div>
          )}
        </QuerySkeleton>
      )}
    </div>
  );
};

const StatusField = (props: {
  isAffiliate: boolean;
  isPro: boolean;
  customerId: Uuid;
  customerPhoneNumber: string | undefined;
  onChange: () => void;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const generateCodeMut = useClientManager("loyaltyPoints").generateAndAssignAffiliateCode().useMutation();

  if (props.isAffiliate && props.isPro) return "Partner, PRO";
  if (props.isAffiliate) return "Partner";
  if (props.isPro) return "PRO";

  const onConfirm = async () => {
    await generateCodeMut.mutateAsync({ userUuid: props.customerId, notifyUser: false });
    setIsDialogOpen(false);

    props.onChange();
  };

  return (
    <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
      -
      <Button size="small" variant="text" onClick={() => setIsDialogOpen(true)}>
        Wygeneruj kod partnera
      </Button>
      {isDialogOpen && (
        <Dialog
          css={{ maxWidth: "400px" }}
          onClose={() => setIsDialogOpen(false)}
          footer={
            <div css={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", width: "100%" }}>
              <Button variant="outline" color="danger" onClick={() => setIsDialogOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={onConfirm}>Wygeneruj</Button>
            </div>
          }
          header="Wygeneruj kod partnera"
        >
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div>Czy na pewno chcesz wygenerować nowy kod zaproszeniowy? </div>
            <div>
              Konto klienta zyska status <strong>PARTNER</strong>.
            </div>
            <div>Klient będzie mógł wyświetlić zaproszenie w aplikacji Specjalista.</div>
            <DescriptionField label="Numer telefonu">{props.customerPhoneNumber}</DescriptionField>
          </div>
        </Dialog>
      )}
    </div>
  );
};

function createUrl(pathname: string, params: Record<string, any>) {
  const searchText = JSON.stringify(params);

  return `${pathname}?filters=${encodeURIComponent(searchText)}`;
}
